import React, { useState } from 'react'
import { Box, Button, Dialog, DialogContent, DialogTitle, TextField, Typography } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format, startOfMonth, subMonths } from 'date-fns'

import { useOpenJournalReconPeriodMutation } from '../../../../api-services/financial/journalReconApi'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch } from '../../../../app/hooks'
import { setNotification } from '../../../components/ui-widgets/uiWidgetsSlice'
import ButtonLoadingComponent from '../../../../views/components/shared/ButtonLoadingComponent'

interface Props {
  onCloseDialog: () => void
  onSuccess: (periodStartAt: string) => void
}

const API_DATE_FORMAT = 'dd-MMM-yyyy'

const OpenJournalReconPeriodDialog: React.FC<Props> = ({ onCloseDialog, onSuccess }) => {
  const dispatch = useAppDispatch()
  const [selectedMonth, setSelectedMonth] = useState<Date>(startOfMonth(subMonths(new Date(), 1)))
  const [apiOpenPeriod, { isLoading }] = useOpenJournalReconPeriodMutation()

  const handleOpenPeriod = (): void => {
    const startDateForApi = format(startOfMonth(selectedMonth), API_DATE_FORMAT)
    apiOpenPeriod(startDateForApi)
      .unwrap()
      .then(() => {
        dispatch(setNotification({ open: true, severity: 'success', message: `Period ${startDateForApi} opened.` }))
        onSuccess(startDateForApi)
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  return (
    <Dialog
      open={true}
      maxWidth={'sm'}
      fullWidth={true}
      onClose={() => { onCloseDialog() }}
    >
      <DialogTitle>Open New Journal Period</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant={'body1'} sx={{ mb: 2 }}>
            Choose the month to generate journals for. The system will use the first day of the selected month
            as the period start date.
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              views={['year', 'month']}
              label='Period Month'
              value={selectedMonth}
              onChange={(newValue: Date | null) => {
                if (newValue !== null) {
                  setSelectedMonth(startOfMonth(newValue))
                }
              }}
              renderInput={(params: any) => (
                <TextField {...params} sx={{ width: '100%' }} />
              )}
            />
          </LocalizationProvider>
        </Box>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button variant={'contained'} onClick={handleOpenPeriod} disabled={isLoading}>
            Open Period
            { isLoading && <ButtonLoadingComponent /> }
          </Button>
          <Button variant={'outlined'} onClick={() => { onCloseDialog() }} disabled={isLoading}>
            Cancel
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default OpenJournalReconPeriodDialog
